import { useState } from 'react';

const EMPTY_IMAGES: never[] = [];

/**
 * Day selection is local to a cage/bioscope. Reset it when the id changes so a
 * day chosen on the previous entity is not sent as `?day=` on the new list query.
 */
export function useSelectedDayForEntity(entityId: number): {
    selectedDay: string | undefined;
    onDayChange: (day: string) => void;
} {
    const [trackedId, setTrackedId] = useState(entityId);
    const [selectedDay, setSelectedDay] = useState<string | undefined>();

    if (trackedId !== entityId) {
        setTrackedId(entityId);
        setSelectedDay(undefined);
    }

    return {
        selectedDay: trackedId === entityId ? selectedDay : undefined,
        onDayChange: setSelectedDay,
    };
}

/**
 * `keepPreviousData` is for day changes on the same entity. When the cage or
 * bioscope id changes, previous rows are other images remapped onto the new
 * entity's JPEG URLs (404 / broken icons). Drop them and treat it as a load.
 */
export function useScopeImagesToEntity<T>(
    entityId: number,
    isPreviousData: boolean,
    isQueryLoading: boolean,
    isQueryError: boolean,
    images: T[]
): {
    images: T[];
    isLoading: boolean;
    isPreviousData: boolean;
    isError: boolean;
} {
    const [ownerId, setOwnerId] = useState(entityId);
    // While previous data is showing, `ownerId` is the entity that produced it.
    // Once the current query has its own data, treat this entity as the owner
    // immediately so we do not flash an empty state for one render.
    const effectiveOwnerId = isPreviousData ? ownerId : entityId;
    if (!isPreviousData && ownerId !== entityId) {
        setOwnerId(entityId);
    }

    const belongsToEntity = effectiveOwnerId === entityId;
    // A fresh `[]` every render looks like a new image list to useImageState,
    // which setStates during render and loops when switching cages.
    const scopedImages = belongsToEntity ? images : EMPTY_IMAGES;

    return {
        images: scopedImages,
        isLoading: isQueryLoading || (isPreviousData && !belongsToEntity),
        isPreviousData: isPreviousData && belongsToEntity,
        isError: Boolean(isQueryError && scopedImages.length === 0),
    };
}
